import { solve } from './solver';
import { RuleId } from '../shared/rules';
import {
  BANDS, DifficultyBand, PuzzleDifficulty, cheapDifficulty, computeDifficulty, predictedSolveMsFromCheap,
} from '../shared/analysis/difficulty';

/** Color labels available to the editor, one per region. Index aligns with the
 *  `.color-N` palette classes in style.css (which currently defines 0..13). */
export const COLOR_LABELS = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N'];

export const MAX_SIZE = 12;
export const MIN_SIZE = 4;

/** Default solver cap for live counting / reduction. We never need the true count
 *  beyond a handful — "0 / 1 / many" is what drives the editor — so a small cap
 *  keeps the backtracking solver fast even at 12×12. */
export const DEFAULT_CAP = 50;

/** A fresh size×size grid with one color per column (a valid starting partition). */
export function blankRegions(size: number): string[][] {
  return Array.from({ length: size }, () =>
    Array.from({ length: size }, (_, c) => COLOR_LABELS[c])
  );
}

export function cloneRegions(regions: string[][]): string[][] {
  return regions.map(row => [...row]);
}

function regionsEqual(a: string[][], b: string[][]): boolean {
  if (a.length !== b.length) return false;
  for (let r = 0; r < a.length; r++) {
    for (let c = 0; c < a.length; c++) if (a[r][c] !== b[r][c]) return false;
  }
  return true;
}

function solutionsEqual(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

export interface SolutionInfo {
  count: number;       // number of solutions found (capped)
  capped: boolean;     // true if we stopped at the cap (real count may be higher)
  solutions: number[][];
}

/** Counts solutions up to `cap`. count===1 && !capped ⇒ unique. */
export function countSolutions(regions: string[][], cap = DEFAULT_CAP): SolutionInfo {
  const solutions = solve(regions, cap);
  return { count: solutions.length, capped: solutions.length >= cap, solutions };
}

// ---- Region validity ----

/** All cells of `color`, excluding an optional removed cell, form one orthogonally
 *  connected group (and are non-empty). Used to keep regions contiguous when editing. */
export function regionConnected(
  regions: string[][],
  color: string,
  exclude?: { row: number; col: number }
): boolean {
  const size = regions.length;
  const cells: Array<[number, number]> = [];
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (regions[r][c] !== color) continue;
      if (exclude && exclude.row === r && exclude.col === c) continue;
      cells.push([r, c]);
    }
  }
  if (cells.length === 0) return false;

  const seen = new Set<string>([`${cells[0][0]},${cells[0][1]}`]);
  const stack = [cells[0]];
  while (stack.length) {
    const [r, c] = stack.pop()!;
    for (const [dr, dc] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) {
      const nr = r + dr, nc = c + dc;
      if (nr < 0 || nr >= size || nc < 0 || nc >= size) continue;
      if (exclude && exclude.row === nr && exclude.col === nc) continue;
      if (regions[nr][nc] !== color) continue;
      const key = `${nr},${nc}`;
      if (seen.has(key)) continue;
      seen.add(key);
      stack.push([nr, nc]);
    }
  }
  return seen.size === cells.length;
}

export interface PuzzleValidity {
  size: number;
  colorCount: number;
  /** Colors whose cells are split into more than one piece. */
  brokenRegions: string[];
  /** A proper N×N puzzle uses exactly N colors and all regions are contiguous. */
  ok: boolean;
}

export function validateRegions(regions: string[][]): PuzzleValidity {
  const size = regions.length;
  const colors = new Set<string>();
  for (const row of regions) for (const cell of row) colors.add(cell);

  const brokenRegions: string[] = [];
  for (const color of colors) {
    if (!regionConnected(regions, color)) brokenRegions.push(color);
  }

  return {
    size,
    colorCount: colors.size,
    brokenRegions: brokenRegions.sort(),
    ok: colors.size === size && brokenRegions.length === 0,
  };
}

// ---- Guided reduce-to-unique ----

export interface ReduceOptions {
  cap?: number;
  maxIterations?: number;
  /** Only cells for which this returns true may be recolored. Defaults to all. */
  editable?: (row: number, col: number) => boolean;
  /** How many top-ranked candidate cells to actually re-solve each step. */
  topK?: number;
  /** Solution to keep valid throughout. Must be a current solution; otherwise the
   *  first solution found is used. */
  target?: number[];
  /** Epoch-ms deadline; iteration stops once passed (reported as 'maxIterations'). */
  deadline?: number;
  /** Never recolor a cell out of a region at or below this size. Defaults to 1
   *  (regions merely can't be emptied). */
  minRegionSize?: number;
}

export interface ReduceResult {
  regions: string[][];
  solutionCount: number;
  unique: boolean;
  steps: number;
  reason: 'unique' | 'stuck' | 'maxIterations' | 'unsolvable';
}

interface Candidate {
  row: number;
  col: number;
  toColor: string;
  rivalsThrough: number; // exact number of current rivals this recolor kills
}

/** Orthogonal neighbor colors of (r,c) other than its own color. */
function neighborColors(regions: string[][], r: number, c: number): string[] {
  const size = regions.length;
  const own = regions[r][c];
  const out = new Set<string>();
  for (const [dr, dc] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) {
    const nr = r + dr, nc = c + dc;
    if (nr < 0 || nr >= size || nc < 0 || nc >= size) continue;
    if (regions[nr][nc] !== own) out.add(regions[nr][nc]);
  }
  return [...out];
}

/**
 * Generate the legal recolorings that could kill rivals.
 *
 * Insight: in a proper N-color puzzle every solution is a bijection over colors,
 * so recoloring a cell `x` that a solution passes through always creates a
 * duplicate-color collision and kills that solution — regardless of which
 * neighbor color we paint it. Therefore the number of rivals a recolor kills is
 * exactly how many rivals pass through `x` (rivalsThrough). We only need the
 * solver afterward to confirm the *net* effect (a recolor can also create new
 * solutions, which is the only color-dependent part).
 *
 * Candidate cells are the rival cells that are (a) not on the protected target
 * solution, (b) editable, and (c) removable from their region without breaking
 * its contiguity. Target cells are excluded so the target stays valid.
 */
function generateCandidates(
  regions: string[][],
  rivals: number[][],
  target: number[],
  editable: (row: number, col: number) => boolean,
  minRegionSize = 1
): Candidate[] {
  const size = regions.length;
  const rivalsThrough: number[][] = Array.from({ length: size }, () => new Array(size).fill(0));
  for (const q of rivals) {
    for (let r = 0; r < size; r++) rivalsThrough[r][q[r]]++;
  }

  const counts = minRegionSize > 1 ? colorCounts(regions) : null;
  const candidates: Candidate[] = [];
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (rivalsThrough[r][c] === 0) continue;
      if (target[r] === c) continue;           // never break the target
      if (!editable(r, c)) continue;
      const own = regions[r][c];
      if (counts && counts.get(own)! <= minRegionSize) continue;
      // Removing this cell from its region must keep that region contiguous & non-empty.
      if (!regionConnected(regions, own, { row: r, col: c })) continue;
      for (const toColor of neighborColors(regions, r, c)) {
        candidates.push({ row: r, col: c, toColor, rivalsThrough: rivalsThrough[r][c] });
      }
    }
  }
  // Best killers first so the topK we re-solve are the highest-impact moves.
  candidates.sort((a, b) => b.rivalsThrough - a.rivalsThrough);
  return candidates;
}

/**
 * Greedily recolor boundary cells to drive the puzzle toward a unique solution,
 * keeping one target solution valid throughout. Returns the reduced regions and
 * why it stopped. Pure (does not mutate the input).
 */
export function reduceToUnique(regions: string[][], options: ReduceOptions = {}): ReduceResult {
  const cap = options.cap ?? DEFAULT_CAP;
  const maxIterations = options.maxIterations ?? 500;
  const topK = options.topK ?? 12;
  const editable = options.editable ?? (() => true);

  let current = cloneRegions(regions);
  let solutions = solve(current, cap);
  if (solutions.length === 0) {
    return { regions: current, solutionCount: 0, unique: false, steps: 0, reason: 'unsolvable' };
  }

  // Try each present solution as the protected target until one reaches unique.
  // Rotating targets is the escape hatch when a given target gets stuck.
  const triedTargets: number[][] = [];
  let target = options.target && solutions.some(s => solutionsEqual(s, options.target!))
    ? options.target
    : solutions[0];
  let steps = 0;
  const visited = new Set<string>([current.map(r => r.join('')).join('')]);

  for (let iter = 0; iter < maxIterations; iter++) {
    if (solutions.length <= 1) {
      return { regions: current, solutionCount: solutions.length, unique: solutions.length === 1, steps, reason: 'unique' };
    }
    if (options.deadline !== undefined && Date.now() > options.deadline) {
      return { regions: current, solutionCount: solutions.length, unique: false, steps, reason: 'maxIterations' };
    }

    const rivals = solutions.filter(s => !solutionsEqual(s, target));
    const candidates = generateCandidates(current, rivals, target, editable, options.minRegionSize);

    // Evaluate the highest-impact candidates and keep the one that minimizes the
    // resulting count, tie-broken by exact kills (rivalsThrough). When the true
    // count exceeds the cap the count stays saturated, so killing rivals doesn't
    // lower it yet — we still accept the best non-worsening move (and a visited
    // guard prevents cycling) so merging keeps shrinking the real solution set
    // until it drops below the cap and strict reduction finishes the job.
    let best: { regions: string[][]; solutions: number[][]; key: string; rivals: number; count: number } | null = null;

    for (const cand of candidates.slice(0, topK)) {
      const trial = cloneRegions(current);
      trial[cand.row][cand.col] = cand.toColor;
      const key = trial.map(r => r.join('')).join('');
      if (visited.has(key)) continue;
      const trialSolutions = solve(trial, cap);
      if (trialSolutions.length > solutions.length) continue; // never worsen
      const better =
        !best ||
        trialSolutions.length < best.count ||
        (trialSolutions.length === best.count && cand.rivalsThrough > best.rivals);
      if (better) {
        best = { regions: trial, solutions: trialSolutions, key, rivals: cand.rivalsThrough, count: trialSolutions.length };
        if (best.count <= 1) break;
      }
    }

    if (best) {
      current = best.regions;
      solutions = best.solutions;
      visited.add(best.key);
      steps++;
      continue;
    }

    // Stuck with this target — rotate to an untried current solution and retry.
    triedTargets.push(target);
    const next = solutions.find(s => !triedTargets.some(t => solutionsEqual(t, s)));
    if (next) {
      target = next;
      continue;
    }
    return { regions: current, solutionCount: solutions.length, unique: false, steps, reason: 'stuck' };
  }

  return {
    regions: current,
    solutionCount: solutions.length,
    unique: solutions.length === 1,
    steps,
    reason: solutions.length === 1 ? 'unique' : 'maxIterations',
  };
}

/**
 * All geometric placements (one queen per row, distinct columns, no diagonal
 * adjacency) ignoring color — the structural skeleton a coloring must turn into a
 * solution. Capped because the count grows quickly with size.
 */
function geometricPlacements(size: number, cap: number): number[][] {
  const out: number[][] = [];
  const cols = new Array<number>(size);
  const used = new Array<boolean>(size).fill(false);
  function bt(row: number) {
    if (out.length >= cap) return;
    if (row === size) { out.push(cols.slice()); return; }
    for (let c = 0; c < size; c++) {
      if (used[c]) continue;
      if (row > 0 && Math.abs(cols[row - 1] - c) === 1) continue;
      cols[row] = c;
      used[c] = true;
      bt(row + 1);
      used[c] = false;
    }
  }
  bt(0);
  return out;
}

function distinctColorCount(regions: string[][], placement: number[]): number {
  const seen = new Set<string>();
  for (let r = 0; r < placement.length; r++) seen.add(regions[r][placement[r]]);
  return seen.size;
}

/**
 * Try to recolor a placement's cells so they hit `size` distinct colors, which
 * makes the placement a valid solution. Each step recolors a duplicated cell to a
 * color missing from the placement — preferring an adjacent existing color, and
 * falling back to a brand-new region label when the grid has fewer than `size`
 * colors (the over-merged case). Keeps every region contiguous. Returns the fixed
 * regions, or null if this placement can't be fixed under the constraints.
 */
function fixPlacement(
  regions: string[][],
  placement: number[],
  editable: (row: number, col: number) => boolean
): string[][] | null {
  const size = regions.length;
  let current = cloneRegions(regions);

  for (let guard = 0; guard < size * size; guard++) {
    const colorAt = placement.map((col, row) => current[row][col]);
    const counts = new Map<string, number>();
    for (const color of colorAt) counts.set(color, (counts.get(color) ?? 0) + 1);
    if (counts.size === size) return current; // bijection ⇒ valid solution

    const inPlacement = new Set(colorAt);
    const allColors = new Set<string>();
    for (const row of current) for (const cell of row) allColors.add(cell);
    const existingMissing = [...allColors].filter(c => !inPlacement.has(c));
    const newLabel = allColors.size < size
      ? COLOR_LABELS.slice(0, size).find(l => !allColors.has(l))
      : undefined;

    let moved = false;
    for (let row = 0; row < size && !moved; row++) {
      const col = placement[row];
      if (counts.get(current[row][col])! < 2) continue; // only duplicated cells help
      if (!editable(row, col)) continue;
      const own = current[row][col];
      if (!regionConnected(current, own, { row, col })) continue; // keep its region whole

      // Prefer reusing an adjacent existing color that's missing from the placement.
      const adjacentMissing = neighborColors(current, row, col).find(c => existingMissing.includes(c));
      const target = adjacentMissing ?? newLabel;
      if (!target) continue;

      current = cloneRegions(current);
      current[row][col] = target;
      moved = true;
    }
    if (!moved) return null;
  }
  return null;
}

/**
 * The reverse of reduceToUnique: when a puzzle has no solutions, create one and
 * then settle at exactly one. Picks the geometric placement closest to already
 * being a solution (most distinct colors), fixes its coloring, and reduces.
 * If the puzzle already has solutions it just defers to reduceToUnique.
 */
export function increaseToUnique(regions: string[][], options: ReduceOptions = {}): ReduceResult {
  const cap = options.cap ?? DEFAULT_CAP;
  const editable = options.editable ?? (() => true);

  if (solve(regions, 1).length > 0) {
    return reduceToUnique(regions, options);
  }

  const size = regions.length;
  const placements = geometricPlacements(size, 400)
    .sort((a, b) => distinctColorCount(regions, b) - distinctColorCount(regions, a));

  if (placements.length === 0) {
    return { regions: cloneRegions(regions), solutionCount: 0, unique: false, steps: 0, reason: 'unsolvable' };
  }

  for (const placement of placements) {
    const fixed = fixPlacement(regions, placement, editable);
    if (!fixed) continue;
    // `placement` is now a solution; protect it while reducing any extras away.
    const reduced = reduceToUnique(fixed, { ...options, cap, target: placement });
    if (reduced.solutionCount >= 1) return reduced;
  }

  return { regions: cloneRegions(regions), solutionCount: 0, unique: false, steps: 0, reason: 'stuck' };
}

// ---- Random puzzle generation ----

/**
 * A uniform random geometric placement (one queen per row, distinct columns, no
 * diagonal adjacency): rejection-sample random permutations, falling back to
 * shuffled backtracking for sizes where rejection keeps missing (it rarely does —
 * non-consecutive permutations stay a healthy fraction of all permutations).
 */
function randomPlacement(size: number, random: () => number): number[] {
  const perm = Array.from({ length: size }, (_, i) => i);
  const shuffle = (arr: number[]) => {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  };

  for (let t = 0; t < 200; t++) {
    shuffle(perm);
    let ok = true;
    for (let r = 1; r < size; r++) {
      if (Math.abs(perm[r] - perm[r - 1]) === 1) { ok = false; break; }
    }
    if (ok) return perm.slice();
  }

  // Backtracking fallback with per-row shuffled column order (still random, mildly biased).
  const cols = new Array<number>(size);
  const used = new Array<boolean>(size).fill(false);
  const bt = (row: number): boolean => {
    if (row === size) return true;
    for (const c of shuffle(Array.from({ length: size }, (_, i) => i))) {
      if (used[c]) continue;
      if (row > 0 && Math.abs(cols[row - 1] - c) === 1) continue;
      cols[row] = c;
      used[c] = true;
      if (bt(row + 1)) return true;
      used[c] = false;
    }
    return false;
  };
  bt(0); // always succeeds for size >= 4
  return cols.slice();
}

/**
 * Partition the grid into `size` contiguous regions, one grown around each queen
 * of `placement`, via weighted multi-source flood fill. Random per-region growth
 * weights make some regions sprawl and others stay small, so shapes vary widely
 * between calls. Color labels are shuffled so the palette isn't tied to row order.
 */
function growRegions(
  size: number,
  placement: number[],
  random: () => number,
  minRegionSize = 1
): string[][] | null {
  const regions: (string | null)[][] = Array.from({ length: size }, () => Array(size).fill(null));
  const labels = COLOR_LABELS.slice(0, size);
  for (let i = labels.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [labels[i], labels[j]] = [labels[j], labels[i]];
  }

  // Skewed weights make some regions sprawl and others stay small. That bias is
  // deliberate: small regions constrain the solution count hard, which is what
  // makes reduce-to-unique tractable at larger sizes (flat weights were tried and
  // construction timed out at 12×12). The MCMC polish pass owns fairness — its
  // stationary distribution doesn't depend on the constructor's starting point.
  const weights = new Map<string, number>();
  const sizes = new Map<string, number>();
  for (let r = 0; r < size; r++) {
    regions[r][placement[r]] = labels[r];
    weights.set(labels[r], 0.05 + Math.pow(random(), 2) * 3);
    sizes.set(labels[r], 1);
  }

  let unassigned = size * size - size;
  while (unassigned > 0) {
    // Frontier: empty cells orthogonally touching a region, one entry per (cell, color).
    // While any region is below the size floor, only below-floor regions may grow —
    // that guarantees every region reaches `minRegionSize` before free growth begins.
    const needQuota = minRegionSize > 1 && [...sizes.values()].some(s => s < minRegionSize);
    const frontier: Array<{ r: number; c: number; color: string; w: number }> = [];
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        if (regions[r][c] !== null) continue;
        const seen = new Set<string>();
        for (const [dr, dc] of [[-1, 0], [1, 0], [0, -1], [0, 1]]) {
          const nr = r + dr, nc = c + dc;
          if (nr < 0 || nr >= size || nc < 0 || nc >= size) continue;
          const color = regions[nr][nc];
          if (color === null || seen.has(color)) continue;
          if (needQuota && sizes.get(color)! >= minRegionSize) continue;
          seen.add(color);
          frontier.push({ r, c, color, w: weights.get(color)! });
        }
      }
    }
    if (frontier.length === 0) return null; // a below-floor region got enclosed — resample
    let total = 0;
    for (const f of frontier) total += f.w;
    let pick = random() * total;
    let chosen = frontier[frontier.length - 1];
    for (const f of frontier) {
      pick -= f.w;
      if (pick <= 0) { chosen = f; break; }
    }
    regions[chosen.r][chosen.c] = chosen.color;
    sizes.set(chosen.color, sizes.get(chosen.color)! + 1);
    unassigned--;
  }

  return regions as string[][];
}

function colorCounts(regions: string[][]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const row of regions) for (const cell of row) counts.set(cell, (counts.get(cell) ?? 0) + 1);
  return counts;
}

/** All legal single-cell recolors: the cell's own region stays contiguous and
 *  non-empty without it (and above `minRegionSize`), and the new color comes
 *  from an orthogonal neighbor (which keeps the destination region contiguous
 *  too). */
function legalMoves(
  regions: string[][],
  editable: (row: number, col: number) => boolean = () => true,
  minRegionSize = 1
): Array<{ r: number; c: number; toColor: string }> {
  const size = regions.length;
  const counts = minRegionSize > 1 ? colorCounts(regions) : null;
  const moves: Array<{ r: number; c: number; toColor: string }> = [];
  for (let r = 0; r < size; r++) {
    for (let c = 0; c < size; c++) {
      if (!editable(r, c)) continue;
      if (counts && counts.get(regions[r][c])! <= minRegionSize) continue;
      if (!regionConnected(regions, regions[r][c], { row: r, col: c })) continue;
      for (const toColor of neighborColors(regions, r, c)) {
        moves.push({ r, c, toColor });
      }
    }
  }
  return moves;
}

/**
 * Metropolis–Hastings random walk over the space of *unique-solution* puzzles,
 * with a uniform stationary distribution. Each step proposes a uniformly random
 * legal recolor; the proposal is kept only if the puzzle still has exactly one
 * solution, and then accepted with probability |M(x)| / |M(x')| (the MH
 * correction for asymmetric move counts — without it, boards with fewer legal
 * moves would be oversampled).
 *
 * Every move is reversible (a boundary cell that just switched regions can
 * switch back), so the chain is a reversible walk on the unique subspace and
 * converges toward uniform over the connected component of its start. Used to
 * wash the constructor's bias out of generated puzzles: after enough steps the
 * board "forgets" how it was built.
 */
export function mcmcPolish(
  regions: string[][],
  options: {
    steps?: number; random?: () => number; deadline?: number; minRegionSize?: number;
    /** Optional objective: lower is better. Turns the uniform walk into simulated annealing
     *  over the same unique-solution subspace — same proposals, same uniqueness check, same MH
     *  correction for asymmetric move counts, with an extra Metropolis factor exp(-ΔE/T).
     *  WITHOUT this the walk is uniform, which is the property the generator was built for;
     *  supplying an energy deliberately gives that up in exchange for aiming. */
    energy?: (regions: string[][]) => number;
    /** Annealing temperature in energy units. Fixed, not a schedule: the walk is short. */
    temperature?: number;
    /** Re-evaluate the energy only every k accepted proposals. The cheapest honest energy
     *  available is ~50ms (see cheapDifficulty), so evaluating every step would make a 400-step
     *  walk take 20s. */
    energyEvery?: number;
  } = {}
): string[][] {
  // The walk plateaus after a few hundred accepted moves (measured: 400 vs 2000
  // proposals gave the same region-size distribution at 8×8), so the default
  // buys most of the mixing without much latency; at large sizes the caller's
  // deadline is what actually bounds the walk.
  const steps = options.steps ?? 800;
  const random = options.random ?? Math.random;
  const minRegionSize = options.minRegionSize ?? 1;

  const energy = options.energy;
  const temperature = options.temperature ?? 1;
  const energyEvery = Math.max(1, options.energyEvery ?? 1);

  let current = cloneRegions(regions);
  let moves = legalMoves(current, undefined, minRegionSize);
  let currentEnergy = energy ? energy(current) : 0;
  let best = current;
  let bestEnergy = currentEnergy;
  let sinceEnergy = 0;

  for (let i = 0; i < steps; i++) {
    if (options.deadline !== undefined && Date.now() > options.deadline) break;
    if (moves.length === 0) break;
    const mv = moves[Math.floor(random() * moves.length)];
    const trial = cloneRegions(current);
    trial[mv.r][mv.c] = mv.toColor;
    if (solve(trial, 2).length !== 1) continue; // must stay uniquely solvable
    const trialMoves = legalMoves(trial, undefined, minRegionSize);

    // Standard MH acceptance for the uniform walk…
    if (random() >= moves.length / trialMoves.length) continue;

    if (!energy) {
      current = trial;
      moves = trialMoves;
      continue;
    }

    // …times the Metropolis factor for the objective. Between energy evaluations the walk is
    // uniform again, which is fine: those steps are proposals, not decisions.
    if (++sinceEnergy < energyEvery) {
      current = trial;
      moves = trialMoves;
      continue;
    }
    sinceEnergy = 0;
    const trialEnergy = energy(trial);
    const delta = trialEnergy - currentEnergy;
    if (delta <= 0 || random() < Math.exp(-delta / temperature)) {
      current = trial;
      moves = trialMoves;
      currentEnergy = trialEnergy;
      if (trialEnergy < bestEnergy) { best = trial; bestEnergy = trialEnergy; }
    }
  }
  // With an objective the caller wants the best board seen, not wherever the chain stopped.
  return energy ? best : current;
}

/**
 * Repair pass: grow every region below `floor` back up to it by stealing
 * orthogonally-adjacent cells from donor regions (donors must stay at or above
 * the floor and contiguous), keeping the puzzle uniquely solvable after every
 * steal. Returns null when some region can't be inflated — callers resample.
 * Exists because floor-constrained *reduction* barely converges (measured ~12%
 * per-attempt at 12×12: the floor forbids exactly the sliver-nibbling moves
 * reduction relies on), whereas unconstrained reduction followed by this repair
 * keeps construction fast.
 */
function inflateToFloor(
  regions: string[][],
  floor: number,
  random: () => number,
  deadline?: number
): string[][] | null {
  const size = regions.length;
  let current = cloneRegions(regions);

  for (let guard = 0; guard < size * size; guard++) {
    if (deadline !== undefined && Date.now() > deadline) return null;
    const counts = colorCounts(current);
    const small = new Set([...counts].filter(([, n]) => n < floor).map(([color]) => color));
    if (small.size === 0) return current;

    const donations: Array<{ r: number; c: number; toColor: string }> = [];
    for (let r = 0; r < size; r++) {
      for (let c = 0; c < size; c++) {
        const own = current[r][c];
        if (counts.get(own)! <= floor) continue; // donor must stay at/above the floor
        const targets = neighborColors(current, r, c).filter(col => small.has(col));
        if (targets.length === 0) continue;
        if (!regionConnected(current, own, { row: r, col: c })) continue;
        for (const toColor of targets) donations.push({ r, c, toColor });
      }
    }
    for (let i = donations.length - 1; i > 0; i--) {
      const j = Math.floor(random() * (i + 1));
      [donations[i], donations[j]] = [donations[j], donations[i]];
    }

    let applied = false;
    for (const mv of donations) {
      const trial = cloneRegions(current);
      trial[mv.r][mv.c] = mv.toColor;
      if (solve(trial, 2).length === 1) { current = trial; applied = true; break; }
    }
    if (!applied) return null; // every donation breaks uniqueness — resample
  }
  return null;
}

export interface GenerateResult {
  regions: string[][] | null;
  attempts: number;
  /** The region-size floor this generation ran with (sampled unless overridden). */
  minRegionSize: number;
}

/**
 * Generate a random puzzle with a unique solution. Unlike randomize/reduce (which
 * perturb the current board), this samples fresh from the whole puzzle space each
 * call: pick a uniform random geometric solution, grow random regions around its
 * queens (so the placement is a solution by construction), then — if the coloring
 * admits rival solutions — reduce them away while protecting the target. Attempts
 * that get stuck are discarded and resampled.
 */
export function generatePuzzle(
  size: number,
  options: { cap?: number; maxAttempts?: number; budgetMs?: number; polishSteps?: number; minRegionSize?: number; random?: () => number } = {}
): GenerateResult {
  const cap = options.cap ?? DEFAULT_CAP;
  const maxAttempts = options.maxAttempts ?? 25;
  const budgetMs = options.budgetMs ?? 4000;
  const random = options.random ?? Math.random;
  const overallDeadline = Date.now() + budgetMs;

  // Region-size floor, sampled per generation unless overridden: mostly ≥3 for
  // chunkier boards, sometimes ≥2, occasionally unconstrained (tiny regions are
  // a legitimate part of the puzzle space — see the fair-sampling analysis).
  const roll = random();
  const sampledFloor = options.minRegionSize ?? (roll < 0.2 ? 1 : roll < 0.5 ? 2 : 3);
  // High floors get much harder to construct as size grows (at 12×12 tiny regions
  // are usually load-bearing for uniqueness), so a *sampled* floor degrades one
  // notch as the budget runs down rather than failing the whole press. An
  // explicitly requested floor is never degraded.
  let minRegionSize = sampledFloor;
  const degradeAt = [Date.now() + budgetMs * 0.6, Date.now() + budgetMs * 0.85];

  // Construction gets a unique board; polish then MCMC-walks it around the
  // unique subspace so the final sample doesn't carry the constructor's bias
  // (greedy reduction tends to gnaw regions down to slivers, for example).
  const polish = (regions: string[][], attempts: number): GenerateResult => ({
    regions: mcmcPolish(regions, {
      steps: options.polishSteps,
      random,
      deadline: Math.min(Date.now() + 1500, overallDeadline),
      minRegionSize,
    }),
    attempts,
    minRegionSize,
  });

  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    if (options.minRegionSize === undefined) {
      const notches = degradeAt.filter(mark => Date.now() > mark).length;
      minRegionSize = Math.max(1, sampledFloor - notches);
    }
    const placement = randomPlacement(size, random);
    const grown = growRegions(size, placement, random, minRegionSize);
    if (grown === null) continue; // a region got enclosed below the floor — resample

    const solutions = solve(grown, cap);
    if (solutions.length === 1) return polish(grown, attempt);

    // Short per-attempt leash: fresh samples are cheap, so resampling beats
    // grinding a stubborn board. The overall budget bounds total click latency.
    // Reduction runs unconstrained (a floor there kills its convergence);
    // inflateToFloor then repairs any regions it gnawed below the floor.
    const reduced = reduceToUnique(grown, {
      cap,
      target: placement,
      deadline: Math.min(Date.now() + 400, overallDeadline),
    });
    if (reduced.unique && validateRegions(reduced.regions).ok) {
      const floored = minRegionSize > 1
        ? inflateToFloor(reduced.regions, minRegionSize, random, Math.min(Date.now() + 500, overallDeadline))
        : reduced.regions;
      if (floored) return polish(floored, attempt);
    }
    if (Date.now() > overallDeadline) return { regions: null, attempts: attempt, minRegionSize };
  }

  return { regions: null, attempts: maxAttempts, minRegionSize };
}

/**
 * Escape hatch matching the "randomly expand regions" idea: apply up to `moves`
 * random legal recolorings within the editable area (each keeps regions contiguous
 * and the color count fixed). Returns new regions; callers re-count afterward.
 */
export function randomizeRegions(
  regions: string[][],
  moves = 1,
  editable: (row: number, col: number) => boolean = () => true,
  random: () => number = Math.random
): string[][] {
  let current = cloneRegions(regions);

  for (let m = 0; m < moves; m++) {
    const options = legalMoves(current, editable);
    if (options.length === 0) break;
    const pick = options[Math.floor(random() * options.length)];
    current = cloneRegions(current);
    current[pick.r][pick.c] = pick.toColor;
  }

  return current;
}

export { regionsEqual };

// ---------------------------------------------------------------------------
// Targeted generation
// ---------------------------------------------------------------------------

/**
 * What kind of puzzle the caller wants. Everything is optional; an empty target degrades to
 * plain `generatePuzzle`.
 */
export interface DifficultyTarget {
  /** Inclusive range on predicted solve time — the difficulty scalar, in human wall-clock ms. */
  refSolveMs?: [number, number];
  /** …or say it by name. */
  band?: DifficultyBand;
  /**
   * Techniques the puzzle must not be solvable without, verified by ablation.
   *
   * ⚠️ MEASURED: at 8×8, **95% of generated puzzles require no elimination technique at all**
   * and only R9 is ever required (5%). The rules overlap so heavily that almost every step has
   * an alternative route, so this is a nearly unreachable target for anything but R9 — use
   * `trains` instead if the goal is "practice this technique".
   */
  requireRules?: RuleId[];
  /**
   * "This puzzle exercises technique X": at least `minShare` of the facts are most easily
   * reached by it. This is the handle that actually discriminates — unlike `requireRules`, which
   * asks the much stronger question "is there NO other way", and almost always finds one.
   */
  trains?: { rule: RuleId; minShare: number };
  /** Techniques it must be solvable *without* — "no paired shadow" is a beginner-friendly board. */
  forbidRules?: RuleId[];
  maxDepth?: number;
}

/**
 * Techniques worth offering as a practice target, each with the share of the puzzle's facts
 * that counts as "this puzzle exercises it". Consumed by the editor's Practices select and by
 * Tips & Tricks (which links here to practice one).
 *
 * Deliberately not one threshold for all of them: the reachable share differs per rule and per
 * board size. Measured over 250 fresh generations at each of 6×6 / 8×8 / 10×10, as median → p90
 * (`scripts/_share-rates.ts`, which is also how to re-measure):
 *
 *     R9  31→44   31→44   29→40      R7  17→22   11→16    8→13
 *     R3  11→22   13→25   12→22      R6   6→17   14→30   19→34
 *     R5   6→17    8→22   11→28      R4   6→19    3→17    2→19
 *     R2   0→ 6    0→ 5    0→ 3
 *
 * So a flat 25% would be unreachable for half of them. Each target asks for roughly its own p90
 * instead: high enough that the puzzle really leans on the technique, low enough that rejection
 * sampling lands it in a handful of tries (measured 13–18% of candidates for R4, 16–53% for R7,
 * 14–42% for R3/R5/R6).
 *
 * **R2 is the honest exception.** A line squeeze is nearly always subsumed by a region shadow
 * that reaches the same square more easily, so its share tops out at 14% and its top decile is
 * only 5% — one or two squeezes in a solve. It is offered anyway because it is a real technique
 * a player asks to drill, and the search does land it (6/6 at both 6×6 and 8×8, though at 8×8 it
 * can burn 20s of the budget getting there).
 *
 * Shares drift with size in both directions — R5/R6 get easier on a big board and R4/R7/R2
 * harder — so a 10×10 practice board is likelier to end on a reported near-miss. That is fine:
 * a near-miss still returns the best candidate the search saw, which is what practice wants.
 *
 * Left out: R1 is bookkeeping, and R8 (forced queen) is in every puzzle by construction.
 */
export const TRAINABLE_RULES: ReadonlyArray<{ rule: RuleId; minShare: number }> = [
  { rule: 'R2', minShare: 0.05 },
  { rule: 'R3', minShare: 0.20 },
  { rule: 'R4', minShare: 0.15 },
  { rule: 'R5', minShare: 0.15 },
  { rule: 'R6', minShare: 0.20 },
  { rule: 'R7', minShare: 0.15 },
  { rule: 'R9', minShare: 0.35 },
];

export interface TargetedResult extends GenerateResult {
  /** Full profile of the returned board, or null if generation failed entirely. */
  profile: PuzzleDifficulty | null;
  /** Did it actually hit the target, or is this the closest near-miss? */
  matched: boolean;
  /** Boards generated and profiled. */
  candidates: number;
  /** Boards rejected by the cheap pre-filter before paying for a full profile. */
  cheapRejects: number;
}

function targetRange(t: DifficultyTarget): [number, number] | null {
  if (t.refSolveMs) return t.refSolveMs;
  if (!t.band) return null;
  let lo = 0;
  for (const b of BANDS) {
    if (b.band === t.band) return [lo, b.maxMs];
    lo = b.maxMs;
  }
  return null;
}

/** Share of the puzzle's facts whose easiest route is `rule`. */
export function ruleShare(p: PuzzleDifficulty, rule: RuleId): number {
  let total = 0, mine = 0;
  for (const [key, n] of Object.entries(p.techniqueSpectrum)) {
    total += n;
    if (key.startsWith(rule + '/')) mine += n;
  }
  return total ? mine / total : 0;
}

/**
 * 0 = a perfect hit; larger is further away. Used to rank near-misses — including across
 * several `generateForTarget` calls, which is how the editor slices a long search into
 * repaintable chunks.
 */
export function targetDistance(p: PuzzleDifficulty, t: DifficultyTarget): number {
  let d = 0;
  const range = targetRange(t);
  if (range) {
    const [lo, hi] = range;
    if (p.refSolveMs < lo) d += ((lo - p.refSolveMs) / Math.max(1, lo)) ** 2;
    else if (p.refSolveMs > hi && isFinite(hi)) d += ((p.refSolveMs - hi) / hi) ** 2;
  }
  // A missing required technique is a hard miss, not a near one: weight it far above any
  // plausible time error so ranking never prefers "right speed, wrong technique".
  for (const r of t.requireRules ?? []) if (!p.requiredTechniques.includes(r)) d += 10;
  for (const r of t.forbidRules ?? []) if (p.requiredTechniques.includes(r)) d += 10;
  if (t.trains) {
    const share = ruleShare(p, t.trains.rule);
    if (share < t.trains.minShare) d += 10 * (t.trains.minShare - share);
  }
  if (t.maxDepth !== undefined && p.depth > t.maxDepth) d += (p.depth - t.maxDepth) * 2;
  return d;
}

export function matchesTarget(p: PuzzleDifficulty, t: DifficultyTarget): boolean {
  return targetDistance(p, t) === 0;
}

/**
 * Generate a puzzle that hits a difficulty or technique target, by rejection sampling.
 *
 * Stage A of the plan, and — measured — the stage that does the work. Each candidate costs a
 * `generatePuzzle` plus a profile, so the budget buys tens of candidates, not thousands. Two
 * things keep that affordable:
 *
 *  - a **cheap pre-filter**. `cheapDifficulty` runs the closure once (~50ms) and reports exactly
 *    which techniques the closure *uses*. A technique the closure never uses cannot be one the
 *    puzzle requires, so a required-rule target rejects most candidates without ever paying for
 *    the nine ablation closures.
 *  - **ablation only when asked for**. A pure time target skips it entirely (~300ms/candidate
 *    instead of ~1.5s).
 *
 * Always returns the closest board it found rather than nothing, with `matched` saying whether
 * it is a hit — a generate button that sometimes returns an empty board is worse than one that
 * returns "closest I could do".
 */
export function generateForTarget(
  size: number,
  target: DifficultyTarget,
  options: {
    budgetMs?: number;
    maxCandidates?: number;
    random?: () => number;
    minRegionSize?: number;
    /** Per-candidate simulation traces. Fewer is noisier; 16 keeps the standard error ~6%. */
    traces?: number;
    onProgress?: (candidates: number, best: number) => void;
  } = {}
): TargetedResult {
  const budgetMs = options.budgetMs ?? 20_000;
  // High by design: the time budget is what should bind, not an arbitrary count. A band target
  // costs ~0.35s per candidate and a rule target ~1.5s, so the same wall-clock buys very
  // different numbers of tries and a fixed cap would silently give up early on the cheap ones.
  const maxCandidates = options.maxCandidates ?? 500;
  const random = options.random ?? Math.random;
  const traces = options.traces ?? 16;
  const deadline = Date.now() + budgetMs;
  const needsAblation = Boolean(target.requireRules?.length || target.forbidRules?.length);

  let best: { regions: string[][]; profile: PuzzleDifficulty; d: number } | null = null;
  let candidates = 0;
  let cheapRejects = 0;
  let attempts = 0;
  let minRegionSize = options.minRegionSize ?? 1;

  while (candidates < maxCandidates && Date.now() < deadline) {
    const gen = generatePuzzle(size, {
      random,
      minRegionSize: options.minRegionSize,
      budgetMs: Math.min(4000, Math.max(500, deadline - Date.now())),
    });
    attempts += gen.attempts;
    minRegionSize = gen.minRegionSize;
    if (!gen.regions) break;

    // Cheap pre-filter: exact about which techniques the closure uses, so it can rule a
    // candidate out for a rule target without paying for ablation.
    if (needsAblation || target.trains) {
      const cheap = cheapDifficulty(gen.regions);
      if (!cheap.solved) { cheapRejects++; continue; }
      const missing = (target.requireRules ?? []).some(r => !cheap.usedRules.has(r));
      // A technique the closure never uses can be neither required nor trained.
      const untrained = target.trains ? !cheap.usedRules.has(target.trains.rule) : false;
      if (missing || untrained) { cheapRejects++; continue; }
    }

    const profile = computeDifficulty(gen.regions, {
      traces, seed: 1 + candidates, skipAblation: !needsAblation,
    });
    candidates++;
    const d = targetDistance(profile, target);
    if (!best || d < best.d) best = { regions: gen.regions, profile, d };
    options.onProgress?.(candidates, best.d);
    if (d === 0) break;
  }

  return {
    regions: best?.regions ?? null,
    profile: best?.profile ?? null,
    matched: best?.d === 0,
    candidates,
    cheapRejects,
    attempts,
    minRegionSize,
  };
}

/**
 * An objective for `mcmcPolish`, built from a target.
 *
 * Honest health warning: the time half rests on `cheapDifficulty`, whose rank correlation with
 * the real profile is 0.64 — enough to push a board in the right direction, not enough to land
 * it on a narrow range. (Calibrated it predicts *player* times to ~20%, but that is a statement
 * about a population of puzzles, not about steering one board to a 5s window.) The *technique*
 * half is exact (the closure either uses a rule or it does not). So this is worth annealing
 * against for structural targets and mostly is not for time targets, where rejection sampling is
 * both simpler and better.
 */
export function targetEnergy(target: DifficultyTarget): (regions: string[][]) => number {
  const range = targetRange(target);
  return (regions: string[][]) => {
    const cheap = cheapDifficulty(regions);
    let e = 0;
    if (!cheap.solved) e += 25;
    if (range) {
      const [lo, hi] = range;
      const mid = isFinite(hi) ? (lo + hi) / 2 : lo * 2;
      // Through the surrogate's own calibration, so this is comparable with the target range,
      // which is in predicted human ms.
      const est = predictedSolveMsFromCheap(cheap.estimateMs, regions.length);
      e += ((est - mid) / Math.max(1000, mid)) ** 2;
    }
    for (const r of target.requireRules ?? []) if (!cheap.usedRules.has(r)) e += 10;
    for (const r of target.forbidRules ?? []) if (cheap.usedRules.has(r)) e += 10;
    if (target.trains && !cheap.usedSigs.size) e += 10;
    else if (target.trains) {
      let total = 0, mine = 0;
      for (const k of cheap.usedSigs) { total++; if (k.startsWith(target.trains.rule + '/')) mine++; }
      const share = total ? mine / total : 0;
      if (share < target.trains.minShare) e += 10 * (target.trains.minShare - share);
    }
    if (target.maxDepth !== undefined && cheap.rounds > target.maxDepth) e += (cheap.rounds - target.maxDepth) * 2;
    return e;
  };
}
